import { lazy, Suspense, useEffect, useState } from "react";
import LoadingIcon from "~/components/form/LoadingIcon";

import { viewWorklistRoute } from "~/pages/all-routes";
import {
  fetchFirstBatch,
  fetchRemainingPages,
  cancelLoading,
} from "~/services/SEFL/zoning-loader.services";

// Define types for the API response
interface ZoningDocuments {
  imageurl: string[];
  jsonurl: string[];
  jsonData?: any;
  taskId?: string;
}

interface TaskData {
  data: {
    zoningdocuments?: ZoningDocuments;
    [key: string]: any;
  };
  [key: string]: any;
}

interface Services {
  getAgentTaskDetailsById: (taskId: string, roleId: string) => Promise<any>;
  [key: string]: any;
}

const getClientComponent = () => {
  const [client] = import.meta.env.MODE.split(":");
  console.log({ client });
  if (!client) {
    throw new Error("Invalid client. Check package.json.");
  }
  return lazy(
    () =>
      import(
        `~/components/pages/agent/document-declaration/${client}/index.tsx`
      )
  );
};

const getServices = (client: string): Promise<Services> => {
  return import(`~/services/${client}/agent.services.ts`)
    .catch(() => {
      console.log(`No specific services found for ${client}, using default services`);
      throw new Error(`No specific services found for ${client}, using default services`);
    });
};


const getProviderComponents = (client: string) => {
  // Base agent provider
  const AgentProvider = lazy(() =>
    import(`~/hooks/${client}/use-agent.tsx`)
      .then(module => {
        if (!module.AgentStateProvider) {
          throw new Error(`AgentStateProvider not found in ${client}/use-agent`);
        }
        return { default: module.AgentStateProvider };
      })
  );

  // Client-specific provider
  const ClientAgentProvider = lazy(() =>
    import(`~/hooks/${client}/use-${client.toLowerCase()}-agent.tsx`)
      .then(module => {
        const providerName = `${client}AgentStateProvider`;
        if (!module[providerName]) {
          // Return a pass-through component if specific provider doesn't exist
          return {
            default: ({ children }: { children: React.ReactNode }) => <>{children}</>
          };
        }
        return { default: module[providerName] };
      })
      .catch(() => {
        console.log(`No specific agent provider found for ${client}`);
        // Return a pass-through component
        return {
          default: ({ children }: { children: React.ReactNode }) => <>{children}</>
        };
      })
  );

  return { AgentProvider, ClientAgentProvider };
};

const roleid = localStorage.getItem("roleid") as string;
const ViewComponent = () => {
  const [data, setData] = useState<TaskData | null>(null);
  const [services, setServices] = useState<Services | null>(null);
  const { taskId } = viewWorklistRoute.useParams();
  const DocumentDeclarationComponent = getClientComponent();

  const [AgentProvider, setAgentProvider] = useState<any>(null);
  const [ClientAgentProvider, setClientAgentProvider] = useState<any>(null);
  const [client] = import.meta.env.MODE.split(":");


  // Load providers and services
  useEffect(() => {
    const { AgentProvider, ClientAgentProvider } = getProviderComponents(client);
    setAgentProvider(AgentProvider);
    setClientAgentProvider(ClientAgentProvider);

    getServices(client)
      .then(setServices)
      .catch(console.error);
  }, [client]);

  useEffect(() => {
    if (taskId == null || taskId === "" || !services) {
      return;
    }

    console.log(taskId);
    async function getData() {
      const response = await services?.getAgentTaskDetailsById(taskId, roleid);
      console.log(response);

      if (response.status && response.data) {
        const taskData = response.data as TaskData;
        const zoningDocs: any = taskData.data?.zoningdocuments;

        // Use progressive loading for zoning documents
        if (zoningDocs?.imageurl?.length > 0 && zoningDocs?.jsonurl?.length > 0) {
          try {
            // Fetch first batch synchronously (shows first N docs instantly)
            const firstBatch = await fetchFirstBatch(
              taskId,
              zoningDocs.imageurl,
              zoningDocs.jsonurl
            );

            // Enrich the data with first batch
            const enrichedData: TaskData = {
              ...taskData,
              data: {
                ...taskData.data,
                zoningdocuments: {
                  ...zoningDocs,
                  imageurl: firstBatch.imageUrls,
                  jsonData: firstBatch.jsonData,
                  allJsonData: firstBatch.allJsonData,
                  totalPages: firstBatch.totalPages,
                  cachedPages: firstBatch.cachedPages,
                  originalUrls: zoningDocs.imageurl,
                  originalJsonUrls: zoningDocs.jsonurl,
                  taskId,
                },
              },
            };
            setData(enrichedData);

            // Start background loading of remaining pages
            if (firstBatch.totalPages > firstBatch.cachedPages.length) {
              fetchRemainingPages(
                taskId,
                zoningDocs.imageurl,
                zoningDocs.jsonurl,
                firstBatch.cachedPages.length
              ).catch(error => {
                console.error("Background loading failed:", error);
              });
            }
          } catch (error) {
            console.error("Error pre-fetching zoning documents:", error);
            // Fallback: set data without zoning documents
            setData(taskData);
          }
        } else {
          setData(taskData);
        }
      }
    }

    void getData();
  }, [taskId, services]);

  // Clean up object URLs and cancel background loading when component unmounts
  useEffect(() => {
    return () => {
      // Revoke all blob URLs to prevent memory leaks
      const imageUrls = data?.data?.zoningdocuments?.imageurl;
      if (Array.isArray(imageUrls)) {
        imageUrls.forEach(url => {
          if (url?.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
      }
      // Cancel any pending background loading
      const taskIdValue = data?.data?.zoningdocuments?.taskId
      if (taskIdValue) {
        cancelLoading(taskIdValue);
      }
    };
  }, [data]);

  if (taskId == null || taskId === "") {
    return <h1>Invalid Task Id</h1>;
  }

  if (!data) {
    return <LoadingIcon />;
  }

  return (
    <Suspense fallback={<LoadingIcon />}>
      <AgentProvider isReadOnly={true}>
        <ClientAgentProvider isReadOnly={true}>
          {data != null && (
            <DocumentDeclarationComponent data={data.data} isReadOnly={true} />
          )}
        </ClientAgentProvider>
      </AgentProvider>
    </Suspense>
  );
};

export default ViewComponent;